import React, { useState } from "react";
import {
  Box,
  Button,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import * as XLSX from "xlsx";
import FileSaver from "file-saver";

/**
 * Plano 21/10/2026 (Frentes 7 e 8, decisão D1): lista com COPIAR LISTA (texto separado por
 * tabulação — cola direto no Excel, como os avisos de impacto dos Produtos N1) e BAIXAR EXCEL.
 * Usada na recusa da planilha de preços (barras fora do cadastro) e no aviso de clientes
 * afetados das Liberações Diversas.
 *
 * `obterLinhas` permite buscar dados extras só quando o usuário pede (ex.: nomes dos clientes
 * na Extranet); sem ele, copia/baixa as linhas exibidas.
 */

export interface ColunaLista {
  titulo: string;
}

interface ListaCopiarBaixarProps {
  colunas: ColunaLista[];
  linhas: (string | number)[][];
  nomeArquivo: string;
  alturaMaxima?: number;
  obterLinhas?: () => Promise<{ colunas: ColunaLista[]; linhas: (string | number)[][] }>;
}

const TIPO_ARQUIVO_XLSX = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

export const ListaCopiarBaixar: React.FC<ListaCopiarBaixarProps> = ({
  colunas,
  linhas,
  nomeArquivo,
  alturaMaxima = 320,
  obterLinhas,
}) => {
  const [copiado, setCopiado] = useState(false);
  const [ocupado, setOcupado] = useState(false);

  const dadosParaExportar = async () => {
    if (!obterLinhas) return { colunas, linhas };
    setOcupado(true);
    try {
      return await obterLinhas();
    } finally {
      setOcupado(false);
    }
  };

  const handleCopiar = async () => {
    const dados = await dadosParaExportar();
    try {
      const tsv = [dados.colunas.map((c) => c.titulo), ...dados.linhas.map((l) => l.map(String))]
        .map((l) => l.join("\t"))
        .join("\n");
      await navigator.clipboard.writeText(tsv);
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2000);
    } catch {
      setCopiado(false);
    }
  };

  const handleBaixar = async () => {
    const dados = await dadosParaExportar();
    const planilha = XLSX.utils.aoa_to_sheet([dados.colunas.map((c) => c.titulo), ...dados.linhas]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, planilha, "Lista");
    const buffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    FileSaver.saveAs(new Blob([buffer], { type: TIPO_ARQUIVO_XLSX }), `${nomeArquivo}.xlsx`);
  };

  return (
    <Box>
      <TableContainer sx={{ maxHeight: alturaMaxima, border: "1px solid #e0e0e0" }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {colunas.map((c) => (
                <TableCell key={c.titulo} sx={{ fontWeight: 600 }}>
                  {c.titulo}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {linhas.map((linha, indice) => (
              <TableRow key={indice}>
                {linha.map((valor, i) => (
                  <TableCell key={i}>{valor}</TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 1 }}>
        <Button
          onClick={handleCopiar}
          disabled={ocupado || linhas.length === 0}
          variant="outlined"
          size="small"
          sx={{ borderColor: "#3C90AD", color: "#3C90AD" }}
        >
          {copiado ? "COPIADO!" : "COPIAR LISTA"}
        </Button>
        <Button
          onClick={handleBaixar}
          disabled={ocupado || linhas.length === 0}
          variant="outlined"
          size="small"
          sx={{ borderColor: "#3C90AD", color: "#3C90AD" }}
        >
          BAIXAR EXCEL
        </Button>
      </Stack>
    </Box>
  );
};

export default ListaCopiarBaixar;
